import { Button } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";
import type { Map } from "../lib/types";
import { downloadUrl } from "../lib/api";
import { getFramePieces, getRenderPieces, viewerModel } from "../lib/pricing";
import { StlViewer } from "./StlViewer";

interface Props {
    map: Map;
    width: string;
    height: string;
    delay?: number;
    // Optional external control: when selectedKey matches one of the files,
    // the carousel jumps to it; whenever the shown file changes (nav buttons,
    // autoplay, or an external jump) onSelect reports the new key upward.
    selectedKey?: string | null;
    onSelect?: (key: string) => void;
}

export function TopographStlCarousel({
    map,
    width,
    height,
    delay = 0,
    selectedKey = null,
    onSelect,
}: Props) {
    const [i, setI] = useState(0);
    const [progress, setProgress] = useState(100);

    const { keys, values } = useMemo(() => {
        const kr = getRenderPieces(map.files);
        const kf = getFramePieces(map.files);
        const ks = kr.concat(kf);
        return { keys: ks, values: ks.map((k) => map.files[k]) };
    }, [map.files]);

    useEffect(() => {
        if (delay <= 0 || keys.length <= 1) return;
        const id = window.setInterval(
            () => setI((prev) => (prev + 1) % keys.length),
            delay * 1000,
        );
        return () => window.clearInterval(id);
    }, [delay, keys.length]);

    // Jump to an externally-selected file (e.g. a tile clicked on the map).
    useEffect(() => {
        if (selectedKey == null) return;
        const idx = keys.indexOf(selectedKey);
        if (idx >= 0) setI(idx);
    }, [selectedKey, keys]);

    // Report the currently-shown file so external UI (the map highlight) can
    // follow along. setState identity is stable, so this can't loop.
    useEffect(() => {
        const k = keys[i];
        if (k) onSelect?.(k);
    }, [i, keys]); // eslint-disable-line react-hooks/exhaustive-deps

    const currentFile = keys[i];
    const currentModel = values[i];
    // Download link uses the .obj (currentModel); the viewer loads the smaller
    // colour-carrying .glb sibling when present, falling back to the .obj.
    const view = currentFile
        ? viewerModel(map.files, currentFile)
        : undefined;

    return (
        <div>
            {Object.values(map.files).map((url) => (
                <link key={url} rel="prefetch" href={url} />
            ))}

            {keys.length > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Button
                        onClick={() =>
                            setI((prev) => (prev - 1 + keys.length) % keys.length)
                        }
                    >
                        🡐
                    </Button>
                    <small>
                        {currentModel && (
                            <a href={downloadUrl(currentModel, currentFile)}>{currentFile}</a>
                        )}{" "}
                        {progress === 100 ? "" : `(${progress}% loaded)`}
                    </small>
                    <Button onClick={() => setI((prev) => (prev + 1) % keys.length)}>🡒</Button>
                </div>
            )}

            <StlViewer
                model={view?.url}
                format={view?.format}
                width={width}
                height={height}
                onProgress={setProgress}
            />
        </div>
    );
}
